import * as fs from 'node:fs'
import * as os from 'node:os'
import * as path from 'node:path'
import * as url from 'node:url'

import delay from 'delay'
import { TimeoutError } from 'p-timeout'
import { Browser, Page, PuppeteerNodeLaunchOptions } from 'puppeteer'
import puppeteer from 'puppeteer-extra'
import RecaptchaPlugin from 'puppeteer-extra-plugin-recaptcha'
import StealthPlugin from 'puppeteer-extra-plugin-stealth'
import random from 'random'

import * as types from './types'
import { getAccessToken, minimizePage } from './utils'

puppeteer.use(StealthPlugin())

const __dirname = url.fileURLToPath(new URL('.', import.meta.url))
const DEFAULT_TIMEOUT_MS = 3 * 60 * 1000 // 3 minutes

/**
 * Represents everything that's required to pass into `ChatGPTAPI` in order
 * to authenticate with the unofficial ChatGPT API.
 */
export type OpenAIAuth = {
  userAgent: string
  accessToken: string
  clearanceToken: string
  sessionToken: string
}

/**
 * Bypasses OpenAI's use of Cloudflare to get the cookies required to use
 * ChatGPT. Uses Puppeteer with a stealth plugin under the hood.
 *
 * If you pass `email` and `password`, then it will log into the account and
 * include a `sessionToken` in the response.
 *
 * If you don't pass `email` and `password`, then it will just return a valid
 * `clearanceToken`.
 *
 * This can be useful because `clearanceToken` expires after ~2 hours, whereas
 * `sessionToken` generally lasts much longer. We recommend renewing your
 * `clearanceToken` every hour or so and creating a new instance of `ChatGPTAPI`
 * with your updated credentials.
 */
export async function getOpenAIAuth({
  email,
  password,
  browser,
  page,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  isGoogleLogin = false,
  isMicrosoftLogin = false,
  nopechaKey = process.env.NOPECHA_KEY,
  capsolverKey = process.env.CAPSOLVER_KEY,
  executablePath,
  proxyServer = process.env.PROXY_SERVER,
  minimize = false
}: {
  email?: string
  password?: string
  browser?: Browser
  page?: Page
  timeoutMs?: number
  isGoogleLogin?: boolean
  isMicrosoftLogin?: boolean
  minimize?: boolean
  nopechaKey?: string
  capsolverKey?: string
  executablePath?: string
  proxyServer?: string
}): Promise<OpenAIAuth> {
  const origBrowser = browser
  const origPage = page

  try {
    if (!browser) {
      browser = await getBrowser({
        nopechaKey,
        capsolverKey,
        executablePath,
        proxyServer,
        timeoutMs
      })
    }

    const userAgent = await browser.userAgent()
    if (!page) {
      page = await getPage(browser, { proxyServer })
      page.setDefaultTimeout(timeoutMs)

      if (minimize) {
        await minimizePage(page)
      }
    }
    let accessToken: string = ''
    await page.goto('https://chatgpt.com/', {
      waitUntil: 'networkidle2'
    })

    await checkForChatGPTAtCapacity(page, { timeoutMs })

    console.log(page.url(), 'page.url()')

    if (page.url() === 'https://chatgpt.com/') {
      const loginButton = await page.waitForSelector(
        '[data-testid="login-button"]',
        {
          timeout: 3000
        }
      )
      console.log(loginButton, 'loginButton')

      await delay(500)
      await page.click('[data-testid="login-button"]')
    } else if (page.url() !== 'https://chatgpt.com/') {
      await waitForConditionOrAtCapacity(page, () =>
        page.waitForSelector('[data-testid="login-button"]', {
          timeout: timeoutMs
        })
      )
      await delay(500)

      // click login button and wait for navigation to finish
      do {
        await Promise.all([
          page.waitForNavigation({
            waitUntil: 'networkidle2',
            timeout: timeoutMs
          }),
          page.click('[data-testid="login-button"]')
        ])
        await delay(500)
      } while (page.url().endsWith('/auth/login'))

      await checkForChatGPTAtCapacity(page, { timeoutMs })
    }

    // once we get to this point, the Cloudflare cookies should be available

    // login as well (optional)
    if (email && password) {
      let submitP: () => Promise<void>

      if (isGoogleLogin) {
        await page.waitForSelector('button[data-provider="google"]', {
          timeout: timeoutMs
        })
        await page.click('button[data-provider="google"]')
        await page.waitForSelector('input[type="email"]')
        await page.type('input[type="email"]', email)
        await Promise.all([
          page.waitForNavigation(),
          await page.keyboard.press('Enter')
        ])
        await page.waitForSelector('input[type="password"]', { visible: true })
        await page.type('input[type="password"]', password)
        await delay(50)
        submitP = () => page.keyboard.press('Enter')
      } else if (isMicrosoftLogin) {
        const [microsoftLogin] = await page.$x(
          '//*[@id="root"]/div/main/section/div[2]/div[3]/button[2]'
        )
        await microsoftLogin.click({
          delay: random.int(5, 25)
        })
        await page.waitForSelector('input[type="email"]')
        await page.type('input[type="email"]', email)
        await Promise.all([
          page.waitForNavigation(),
          await page.keyboard.press('Enter')
        ])
        await delay(1500)
        await page.waitForSelector('input[type="password"]', { visible: true })
        await page.type('input[type="password"]', password)
        await delay(50)
        submitP = () => page.keyboard.press('Enter')
        await Promise.all([
          page.waitForNavigation(),
          await page.keyboard.press('Enter')
        ])
        await delay(1000)
      } else {
        await waitForConditionOrAtCapacity(page, () =>
          page.waitForSelector('[id="email-input"]', {
            timeout: timeoutMs
          })
        )
        await page.type('[id="email-input"]', email)
        await delay(2000)
        const frame = page.mainFrame()
        const submit = await page.waitForSelector('.continue-btn', {
          timeout: timeoutMs
        })
        await frame.focus('.continue-btn')
        await submit.focus()
        await submit.click()
        await delay(3000)
        await waitForConditionOrAtCapacity(page, () =>
          page.waitForSelector('input[type="password"]', {
            timeout: timeoutMs
          })
        )
        await delay(500)
        while (page.url() !== 'https://chatgpt.com/') {
          console.log(page.url(), '未登录')
          await checkForChatGPTAtCapacity(page, { timeoutMs })

          let fun
          try {
            fun = await page.$('div[aria-hidden="false"]')
          } catch (error) {}

          if (page.url().indexOf('/auth/error') !== -1) {
            if (origBrowser) {
              await getOpenAIAuth({
                email,
                password,
                browser,
                page,
                timeoutMs
              })
            } else if (browser) {
              await browser.close()
              await getOpenAIAuth({
                email,
                password
              })
            }
          }
          if (fun) {
            console.log('fun验证')
            await delay(3000)
          } else if (await page.$('input[type="password"]')) {
            await checkForChatGPTAtCapacity(page, { timeoutMs })
            const passInput = await page.$('input[type="password"]')
            const inputValue = await passInput.getProperty('value')
            const value = await inputValue.jsonValue()
            if (!value) {
              await passInput.type(password)
              await delay(1000)
            }
            if (await page.$('button[type="submit"]')) {
              await page.click('button[type="submit"]')
            }
          }
          await delay(8000)
        }
      }
      await delay(10000)
      // await Promise.all([
      //   waitForConditionOrAtCapacity(
      //     page,
      //     async () =>
      //       await page.waitForNavigation({
      //         waitUntil: 'networkidle2',
      //         timeout: timeoutMs
      //       })
      //   ),
      //   submitP()
      // ])
    }
    console.log(page.url(), '登录成功')

    const pageCookies = await page.cookies()
    const cookies = pageCookies.reduce(
      (map, cookie) => ({ ...map, [cookie.name]: cookie }),
      {}
    )
    accessToken = await page.evaluate(getAccessToken)
    const authInfo: OpenAIAuth = {
      userAgent,
      accessToken,
      clearanceToken: cookies['cf_clearance']?.value,
      sessionToken: cookies['__Secure-next-auth.session-token']?.value
    }

    return authInfo
  } catch (err) {
    throw err
  } finally {
    if (origBrowser) {
      if (page && page !== origPage) {
        await page.close()
      }
    } else if (browser) {
      await browser.close()
    }

    page = null
    browser = null
  }
}

export async function getPage(
  browser: Browser,
  opts: {
    proxyServer?: string
  }
) {
  const { proxyServer = process.env.PROXY_SERVER } = opts
  const page = (await browser.pages())[0]

  if (proxyServer && proxyServer.includes('@')) {
    const proxyAuth = proxyServer.split('@')[0].split(':')
    const proxyUsername = proxyAuth[0]
    const proxyPassword = proxyAuth[1]

    try {
      await page.authenticate({
        username: proxyUsername,
        password: proxyPassword
      })
    } catch (err) {
      console.error(
        `ChatGPT "${this._email}" error authenticating proxy "${this._proxyServer}"`,
        err.toString()
      )

      throw err
    }
  }

  return page
}

/**
 * Launches a non-puppeteer instance of Chrome. Note that in my testing, I wasn't
 * able to use the built-in `puppeteer` version of Chromium because Cloudflare
 * recognizes it and blocks access.
 */
export async function getBrowser(
  opts: PuppeteerNodeLaunchOptions & {
    nopechaKey?: string
    capsolverKey?: string
    proxyServer?: string
    minimize?: boolean
    debug?: boolean
    timeoutMs?: number
  } = {}
) {
  const {
    nopechaKey = process.env.NOPECHA_KEY,
    capsolverKey = process.env.CAPSOLVER_KEY,
    executablePath = defaultChromeExecutablePath(),
    proxyServer = process.env.PROXY_SERVER,
    minimize = false,
    debug = false,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    ...launchOptions
  } = opts

  // https://peter.sh/experiments/chromium-command-line-switches/
  const puppeteerArgs = [
    '--no-sandbox',
    '--disable-dev-shm-usage',
    // '--ignore-certificate-errors',
    '--no-first-run',
    '--no-service-autorun',
    '--password-store=basic',
    '--system-developer-mode',
    // the following flags all try to reduce memory
    // '--single-process',
    '--mute-audio',
    '--disable-default-apps',
    '--no-zygote',
    '--disable-accelerated-2d-canvas',
    '--disable-web-security',
    '--disable-gpu',
    `--window-size=1000,800`
  ]

  if (nopechaKey) {
    const nopechaPath = path.join(
      __dirname,
      '..',
      'third-party',
      'nopecha-chrome-extension'
    )
    const nopechaConfigPath = path.join(
      __dirname,
      '..',
      'third-party',
      'nopecha-chrome-extension',
      'manifest.json'
    )
    const data = fs.readFileSync(nopechaConfigPath, 'utf8')
    const newData = data.replace('YOUR KEY HERE', nopechaKey)
    fs.writeFileSync(nopechaConfigPath, newData, 'utf8')
    puppeteerArgs.push(`--disable-extensions-except=${nopechaPath}`)
    puppeteerArgs.push(`--load-extension=${nopechaPath}`)
  } else if (capsolverKey) {
    const capsolverPath = path.join(
      __dirname,
      '..',
      'third-party',
      'capSolver-chrome-extension'
    )
    const capsolverConfigPath = path.join(
      __dirname,
      '..',
      'third-party',
      'capSolver-chrome-extension',
      'assets',
      'config.js'
    )
    const data = fs.readFileSync(capsolverConfigPath, 'utf8')
    const newData = data.replace(
      /(apiKey:\s*')([^\']+)(')/,
      (match, p1, p2, p3) => {
        return `${p1}${capsolverKey}${p3}` // 使用模板字符串动态插入新的 API 键
      }
    )
    fs.writeFileSync(capsolverConfigPath, newData, 'utf8')
    puppeteerArgs.push(`--disable-extensions-except=${capsolverPath}`)
    puppeteerArgs.push(`--load-extension=${capsolverPath}`)
  }

  if (proxyServer) {
    const ipPort = proxyServer.includes('@')
      ? proxyServer.split('@')[1]
      : proxyServer
    puppeteerArgs.push(`--proxy-server=${ipPort}`)
  }

  const browser = await puppeteer.launch({
    headless: false,
    targetFilter: (target) => target.type !== 'other',
    // devtools: true,
    args: puppeteerArgs,
    ignoreDefaultArgs: [
      '--disable-extensions',
      '--enable-automation',
      '--disable-component-extensions-with-background-pages'
    ],
    ignoreHTTPSErrors: true,
    executablePath,
    ...launchOptions
  })
  if (process.env.PROXY_VALIDATE_IP) {
    const page = await getPage(browser, { proxyServer })
    if (minimize) {
      await minimizePage(page)
    }

    // Send a fetch request to https://ifconfig.co using page.evaluate() and
    // verify that the IP matches
    let ip: string
    try {
      const res = await page.evaluate(() => {
        return fetch('https://ifconfig.co', {
          headers: {
            Accept: 'application/json'
          }
        }).then((res) => res.json())
      })

      ip = res?.ip
    } catch (err) {
      throw new Error(`Proxy IP validation failed: ${err.toString()}`, {
        cause: err
      })
    }

    if (!ip || ip !== process.env.PROXY_VALIDATE_IP) {
      throw new Error(
        `Proxy IP mismatch: ${ip} !== ${process.env.PROXY_VALIDATE_IP}`
      )
    }
  }

  return browser
}

/**
 * Gets the default path to chrome's executable for the current platform.
 */
export const defaultChromeExecutablePath = (): string => {
  if (process.env.PUPPETEER_EXECUTABLE_PATH) {
    return process.env.PUPPETEER_EXECUTABLE_PATH
  }

  switch (os.platform()) {
    case 'win32':
      return 'C:\\Program Files\\Google\\Chrome\\Application\\chrome.exe'

    case 'darwin':
      return '/Applications/Google Chrome.app/Contents/MacOS/Google Chrome'

    default: {
      /**
       * Since two (2) separate chrome releases exist on linux, we first do a
       * check to ensure we're executing the right one.
       */
      const chromeExists = fs.existsSync('/usr/bin/chromium')
      if (chromeExists) {
        return '/usr/bin/chromium'
      }

      const piExists = fs.existsSync('/usr/bin/chromium-browser')
      if (piExists) {
        return '/usr/bin/chromium-browser'
      }

      return '/usr/bin/google-chrome-stable'
    }
  }
}

async function checkForChatGPTAtCapacity(
  page: Page,
  opts: {
    timeoutMs?: number
    pollingIntervalMs?: number
    retries?: number
  } = {}
) {
  const {
    timeoutMs = 2 * 60 * 1000, // 2 minutes
    pollingIntervalMs = 3000,
    retries = 10
  } = opts

  // console.log('checkForChatGPTAtCapacity', page.url())
  let isAtCapacity = false
  let numTries = 0

  do {
    try {
      await solveSimpleCaptchas(page)

      const res = await page.$x("//div[contains(., 'ChatGPT is at capacity')]")

      isAtCapacity = !!res?.length

      if (isAtCapacity) {
        if (++numTries >= retries) {
          break
        }

        // try refreshing the page if chatgpt is at capacity
        await page.reload({
          waitUntil: 'networkidle2',
          timeout: timeoutMs
        })

        await delay(pollingIntervalMs)
      }
    } catch (err) {
      // ignore errors likely due to navigation
      ++numTries
      break
    }
  } while (isAtCapacity)

  if (isAtCapacity) {
    const error = new types.ChatGPTError('ChatGPT is at capacity')
    error.statusCode = 503
    throw error
  }
}

async function waitForConditionOrAtCapacity(
  page: Page,
  condition: () => Promise<any>,
  opts: {
    pollingIntervalMs?: number
  } = {}
) {
  const { pollingIntervalMs = 500 } = opts

  return new Promise<void>((resolve, reject) => {
    let resolved = false

    async function waitForCapacityText() {
      if (resolved) {
        return
      }

      try {
        await checkForChatGPTAtCapacity(page)

        if (!resolved) {
          setTimeout(waitForCapacityText, pollingIntervalMs)
        }
      } catch (err) {
        if (!resolved) {
          resolved = true
          return reject(err)
        }
      }
    }

    condition()
      .then(() => {
        if (!resolved) {
          resolved = true
          resolve()
        }
      })
      .catch((err) => {
        if (!resolved) {
          resolved = true
          reject(err)
        }
      })

    setTimeout(waitForCapacityText, pollingIntervalMs)
  })
}

export async function solveSimpleCaptchas(page: Page) {
  try {
    const verifyYouAreHuman = await page.$('#challenge-stage')
    if (verifyYouAreHuman) {
      await delay(2000)
      await verifyYouAreHuman.click({
        delay: random.int(5, 25)
      })
      await delay(1000)
    }

    const cloudflareButton = await page.$('#turnstile-wrapper')
    if (cloudflareButton) {
      await delay(2000)
      await cloudflareButton.click({
        delay: random.int(5, 25)
      })
      await delay(1000)
    }
  } catch (err) {
    // ignore errors
    // console.log(err, 'solveSimpleCaptchasError')
  }
}
